import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import StatusDot from '@/components/StatusDot';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Hand, Pencil, AlertCircle, X, ArrowRight, Filter, CheckCircle, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { ScopeLinkSelect } from '@/components/ScopeLinkSelect';
import api from '@/lib/api';

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [scopeItems, setScopeItems] = useState([]);
  const [scopeNameMap, setScopeNameMap] = useState({});
  const [reminders, setReminders] = useState([]);

  const fetchTasks = async () => {
    const r = await api.get('/tasks');
    const list = Array.isArray(r.data) ? r.data : [];
    setTasks(list);
    const pids = [...new Set(list.filter(t => t.linked_checklist_item_id && t.project_id).map(t => t.project_id))];
    if (pids.length) {
      const results = await Promise.all(pids.map(pid =>
        api.get(`/compliance?project_id=${pid}`).then(res => [pid, res.data]).catch(() => [pid, []])
      ));
      const map = {};
      results.forEach(([pid, data]) => {
        const scope = (Array.isArray(data) ? data : []).find(c => c.template_type === 'Proposal Scope');
        if (scope) {
          map[pid] = {};
          (scope.items || []).forEach(it => { map[pid][it.item_id] = it.activity; });
        }
      });
      setScopeNameMap(map);
    } else {
      setScopeNameMap({});
    }
  };
  const fetchReminders = () => api.get('/action-item-reminders').then(r => setReminders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    fetchTasks();
    fetchReminders();
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Load the selected project's Scope Checklist items (Proposal Scope compliance).
  const loadScopeItems = async (projectId) => {
    if (!projectId) { setScopeItems([]); return; }
    try {
      const r = await api.get(`/compliance?project_id=${projectId}`);
      const list = Array.isArray(r.data) ? r.data : [];
      const scope = list.find(c => c.template_type === 'Proposal Scope');
      setScopeItems(scope && Array.isArray(scope.items) ? scope.items : []);
    } catch { setScopeItems([]); }
  };

  const openCreate = () => {
    setEditTask(null);
    setForm({ title: '', description: '', project_id: '', assigned_to: '', priority: 'Medium', due_date: '', linked_checklist_item_id: '' });
    setScopeItems([]);
    setSheetOpen(true);
  };

  const openEdit = async (t) => {
    setEditTask(t);
    setForm({ ...t });
    if (t.project_id) { await loadScopeItems(t.project_id); }
    else { setScopeItems([]); }
    setSheetOpen(true);
  };

  const handleProjectChange = async (projectId) => {
    setForm({ ...form, project_id: projectId, linked_checklist_item_id: '' });
    await loadScopeItems(projectId);
  };

  const clearProject = () => {
    setForm({ ...form, project_id: '', linked_checklist_item_id: '' });
    setScopeItems([]);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error('Title required');
    try {
      const payload = { ...form };
      delete payload.contract_section_id;
      if (!payload.project_id) {
        payload.project_id = null;
        payload.linked_checklist_item_id = null;
      } else {
        payload.linked_checklist_item_id = payload.linked_checklist_item_id || null;
      }
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, payload);
        toast.success('To-Do updated');
      } else {
        await api.post('/tasks', payload);
        toast.success('To-Do created');
      }
      setSheetOpen(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving To-Do'); }
  };

  const claimTask = async (tid) => {
    try {
      await api.post(`/tasks/${tid}/claim`);
      toast.success('To-Do claimed!');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error claiming'); }
  };

  const dismissReminder = async (reminderId) => {
    await api.put(`/action-item-reminders/${reminderId}/dismiss`);
    setReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  const createTodoFromReminder = async (reminder) => {
    setEditTask(null);
    setForm({
      title: reminder.action_items.length > 80 ? reminder.action_items.substring(0, 80) + '...' : reminder.action_items,
      description: `From status update: ${reminder.action_items}`,
      project_id: reminder.project_id || '',
      assigned_to: '',
      priority: 'Medium',
      due_date: '',
      linked_checklist_item_id: '',
    });
    if (reminder.project_id) { await loadScopeItems(reminder.project_id); }
    else { setScopeItems([]); }
    setSheetOpen(true);
  };

  const updateTaskStatus = async (tid, status) => {
    await api.put(`/tasks/${tid}`, { status });
    toast.success('Status updated');
    fetchTasks();
  };

  // Apply filters
  let filtered = tasks.filter(t =>
    (t.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.project_name || '').toLowerCase().includes(search.toLowerCase())
  );
  if (filterProject) {
    filtered = filtered.filter(t => t.project_id === filterProject);
  }
  if (filterPriority) {
    filtered = filtered.filter(t => t.priority === filterPriority);
  }

  // Split completed vs active
  if (showCompleted) {
    filtered = filtered.filter(t => t.status === 'Completed');
  } else {
    filtered = filtered.filter(t => t.status !== 'Completed');
  }

  // Sort: unclaimed first, then by due date (nearest first, no due date at bottom)
  filtered.sort((a, b) => {
    const aUnclaimed = !a.assigned_to ? 0 : 1;
    const bUnclaimed = !b.assigned_to ? 0 : 1;
    if (aUnclaimed !== bUnclaimed) return aUnclaimed - bUnclaimed;
    const aDate = a.due_date || '';
    const bDate = b.due_date || '';
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return aDate.localeCompare(bDate);
  });

  const hasActiveFilters = filterProject || filterPriority || showCompleted;

  const clearAllFilters = () => {
    setFilterProject('');
    setFilterPriority('');
    setShowCompleted(false);
    setSearch('');
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-lg tracking-tight">To-Dos</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} {showCompleted ? 'completed' : 'active'} to-do{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground" data-testid="create-task-button">
          <Plus className="h-4 w-4 mr-1" /> New To-Do
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search to-dos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="tasks-search" />
        </div>

        <Select value={filterProject} onValueChange={v => setFilterProject(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-9 bg-card border-border text-xs" data-testid="filter-project">
            <Filter className="h-3 w-3 mr-1 shrink-0" />
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Projects</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.project_number} - {p.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={v => setFilterPriority(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 bg-card border-border text-xs" data-testid="filter-priority">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Priorities</SelectItem>
            <SelectItem value="High" className="text-xs">High</SelectItem>
            <SelectItem value="Medium" className="text-xs">Medium</SelectItem>
            <SelectItem value="Low" className="text-xs">Low</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={showCompleted ? 'default' : 'outline'}
          size="sm"
          className={`text-xs h-9 gap-1.5 ${showCompleted ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'border-border'}`}
          onClick={() => setShowCompleted(!showCompleted)}
          data-testid="filter-completed"
        >
          <CheckCircle className="h-3 w-3" /> Completed
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="text-xs h-9 text-muted-foreground hover:text-foreground gap-1" onClick={clearAllFilters} data-testid="clear-all-filters">
            <X className="h-3 w-3" /> Clear Filters
          </Button>
        )}
      </div>

      {/* Action Item Reminders */}
      {reminders.length > 0 && (
        <div className="space-y-2" data-testid="action-item-reminders">
          {reminders.map(r => (
            <div key={r.id} className="flex items-start gap-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/5" data-testid={`reminder-${r.id}`}>
              <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-400 font-medium">{r.project_name}</p>
                <p className="text-sm mt-0.5">{r.action_items}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {r.created_by_name && `By ${r.created_by_name} · `}{new Date(r.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:text-primary/80 gap-1" onClick={() => createTodoFromReminder(r)} data-testid={`reminder-create-${r.id}`}>
                  <ArrowRight className="h-3 w-3" /> Create To-Do
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => dismissReminder(r.id)} data-testid={`reminder-dismiss-${r.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div data-testid="tasks-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">To-Do</TableHead>
              <TableHead className="text-xs text-muted-foreground">Project</TableHead>
              <TableHead className="text-xs text-muted-foreground">Linked Item</TableHead>
              <TableHead className="text-xs text-muted-foreground">Assignee</TableHead>
              <TableHead className="text-xs text-muted-foreground">Priority</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground">Due</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(t => {
              const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && t.status !== 'Completed';
              return (
              <TableRow key={t.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openEdit(t)} data-testid={`task-row-${t.id}`}>
                <TableCell>
                  <p className="text-sm font-medium" style={{ color: '#010A21' }}>{t.title}</p>
                  {t.description && <p className="text-xs text-muted-foreground line-clamp-1">{t.description}</p>}
                </TableCell>
                <TableCell className="text-sm" style={{ color: '#031333' }}>{t.project_name || 'General'}</TableCell>
                <TableCell className="text-sm">
                  {t.linked_checklist_item_id && scopeNameMap[t.project_id]?.[t.linked_checklist_item_id] ? (
                    <span className="inline-flex items-center gap-1" style={{ color: '#1B4A76' }} data-testid={`task-linked-item-${t.id}`}>
                      <Link2 className="h-3 w-3 shrink-0" /> {scopeNameMap[t.project_id][t.linked_checklist_item_id]}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-sm" style={{ color: '#010A21' }}>{t.assignee_name || 'Unassigned'}</TableCell>
                <TableCell className="text-sm" style={{ color: '#031333' }}>{t.priority}</TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <Select value={t.status} onValueChange={v => updateTaskStatus(t.id, v)}>
                    <SelectTrigger className="w-auto h-8 bg-transparent border-0 p-0 gap-1" data-testid={`task-status-${t.id}`}><StatusDot status={t.status} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {['Not Started', 'In Progress', 'Completed', 'On Hold'].map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: overdue ? '#E5441A' : '#010A21' }}>{t.due_date || '\u00A0'}</TableCell>
                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!t.assigned_to && (
                      <Button size="sm" variant="outline" className="h-7 text-sm text-emerald-400 border-emerald-500/20" onClick={() => claimTask(t.id)} data-testid={`claim-task-${t.id}`}>
                        <Hand className="h-3 w-3 mr-1" /> Claim
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            );})}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No to-dos found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">{editTask ? 'Edit To-Do' : 'New To-Do'}</SheetTitle>
            <SheetDescription>{form.project_id ? 'Linked to a project: the scope link is optional' : 'General to-do: no project link'}</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Title *</Label><Input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-background border-border" data-testid="task-form-title" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label><ExpandableTextarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-background border-border" rows={3} expandedRows={8} /></div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Project</Label>
                {form.project_id && <Button variant="ghost" size="sm" className="text-xs h-5 text-muted-foreground" onClick={clearProject}>Clear</Button>}
              </div>
              <Select value={form.project_id || ''} onValueChange={handleProjectChange}>
                <SelectTrigger className="bg-background border-border" data-testid="task-form-project"><SelectValue placeholder="None (General To-Do)" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_number} - {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Link to scope item - only shown when project selected, optional. Populated from the project's Scope Checklist (Proposal Scope). */}
            {form.project_id && (
              <div>
                <ScopeLinkSelect items={scopeItems} value={form.linked_checklist_item_id} onChange={v => setForm({ ...form, linked_checklist_item_id: v })} testid="task-scope-link" />
                {scopeItems.length === 0 && <p className="text-xs text-muted-foreground mt-1">This project has no Scope Checklist items (optional)</p>}
              </div>
            )}

            <div>
              <Label className="text-xs text-muted-foreground">Assign To</Label>
              <Select value={form.assigned_to || ''} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-card border-border">{users.filter(u => u.role !== 'Client' && u.role !== 'Administrator').map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Priority</Label>
                <Select value={form.priority || 'Medium'} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">{['High', 'Medium', 'Low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border" /></div>
            </div>
            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="task-form-submit">{editTask ? 'Update To-Do' : 'Create To-Do'}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
